import { useEffect } from "react";
import { useLocation } from "react-router-dom";

declare global {
  interface Window {
    figma?: {
      captureForDesign: (opts: {
        captureId: string;
        endpoint: string;
        selector?: string;
        verbose?: boolean;
        delayMs?: number;
      }) => Promise<unknown>;
    };
  }
}

/**
 * HashRouter의 `location.search`는 **해시 안**의 쿼리만 본다(`#/cart?figmacapture=…`).
 * `http://localhost:3000/home?figmacapture=…#/cart` 처럼 **# 앞**에만 두면 앱이 못 읽어 캡처가 안 된다.
 * 아래 merge는 그 실수를 완화한다(우선순위: 라우터 search → 해시 쿼리 → `window.location.search`).
 */
function mergeFigmaCaptureSearchParams(
  routerSearch: string,
  hash: string,
  windowSearch: string,
): URLSearchParams {
  const merged = new URLSearchParams();
  const layers = [
    new URLSearchParams(routerSearch),
    hash.includes("?")
      ? new URLSearchParams(hash.slice(hash.indexOf("?") + 1))
      : new URLSearchParams(),
    new URLSearchParams(windowSearch),
  ];
  for (const layer of layers) {
    layer.forEach((value, key) => {
      if (!merged.has(key)) {
        merged.set(key, value);
      }
    });
  }
  return merged;
}

/**
 * Figma html-to-design: capture.js의 cn()은 hash가 "#figmacapture=…"로 시작할 때만 동작한다.
 * HashRouter + index.tsx 정규화 후에는 쿼리가 "#/home?figmacapture=…" 형태라 자동 캡처가 막힌다.
 * 공식 API window.figma.captureForDesign로 동일 플로우를 실행한다(로컬 전용).
 */
export default function FigmaMcpCaptureRunner() {
  const location = useLocation();

  useEffect(() => {
    const clearCaptureHtmlAttrs = () => {
      delete document.documentElement.dataset.figmaCaptureActive;
      delete document.documentElement.dataset.figmaCaptureRoute;
    };

    const hostname = window.location.hostname;
    if (hostname !== "localhost" && hostname !== "127.0.0.1") {
      clearCaptureHtmlAttrs();
      return;
    }

    const params = mergeFigmaCaptureSearchParams(
      location.search,
      window.location.hash,
      typeof window !== "undefined" ? window.location.search : "",
    );
    const captureId = params.get("figmacapture");
    const endpoint = params.get("figmaendpoint");
    if (!captureId || !endpoint) {
      clearCaptureHtmlAttrs();
      return;
    }

    const figmaselector = params.get("figmaselector");
    if (figmaselector === "*") {
      clearCaptureHtmlAttrs();
      return;
    }

    const delayRaw = params.get("figmadelay");
    const delayMs = delayRaw
      ? Math.max(0, parseInt(delayRaw, 10) || 0)
      : 0;
    const resolveCaptureSelector = (): string => {
      if (figmaselector) {
        return figmaselector;
      }
      const path = location.pathname;
      if (
        (path === "/cart" || path === "/checkout") &&
        document.querySelector("[data-figma-capture-root]")
      ) {
        return "[data-figma-capture-root]";
      }
      return "body";
    };
    const verbose = params.get("figmalogverbose") === "true";

    const invokeKey = `figma_mcp_capture_invoked_${captureId}`;

    let cancelled = false;
    const deadline = Date.now() + 60_000;

    document.documentElement.dataset.figmaCaptureActive = "1";
    document.documentElement.dataset.figmaCaptureRoute = location.pathname;

    const poll = () => {
      if (cancelled) {
        return;
      }
      if (window.figma?.captureForDesign) {
        if (sessionStorage.getItem(invokeKey)) {
          return;
        }
        sessionStorage.setItem(invokeKey, "1");
        const resolved = resolveCaptureSelector();
        window.scrollTo(0, 0);
        const rootEl = document.querySelector<HTMLElement>(
          "[data-figma-capture-root]",
        );
        if (rootEl) {
          rootEl.scrollTop = 0;
        }
        void window.figma.captureForDesign({
          captureId,
          endpoint,
          selector: resolved,
          verbose,
          delayMs,
        });
        return;
      }
      if (Date.now() > deadline) {
        console.warn(
          "[Figma MCP] captureForDesign not available (is capture.js loaded?)"
        );
        return;
      }
      requestAnimationFrame(() => setTimeout(poll, 50));
    };

    poll();
    return () => {
      cancelled = true;
      clearCaptureHtmlAttrs();
    };
  }, [location.pathname, location.search]);

  return null;
}
